import { useAccount } from 'wagmi';

import {
  EVMNft,
  EvmNFTChain,
  EvmNFTCollectionContractName,
} from '@repo/types-client';

import { useEvmNfts } from '../evm/hooks';
import { returnChain } from './return-chain';

type UseIsActionableProps = {
  contract: EvmNFTCollectionContractName;
  tokenId: string;
  chain: EvmNFTChain;
};

/**
 * Custom hook to determine if an NFT is actionable based on ownership and chain.
 * @function useIsActionable
 * @param {UseIsActionableProps} props - The properties for the hook.
 * @param {EvmNFTCollectionContractName} props.contract - The contract name of the NFT collection.
 * @param {string} props.tokenId - The token ID of the NFT.
 * @param {EvmNFTChain} props.chain - The blockchain chain of the NFT.
 * @returns {object} An object containing actionable status and related information.
 */
export const useIsActionable = ({
  contract,
  tokenId,
  chain,
}: UseIsActionableProps) => {
  const account = useAccount();

  const { data } = useEvmNfts<EVMNft>({
    address: account.address,
    tokenId,
    collections: [contract],
    chain: chain,
    dataSource: 'onchain',
  });

  return {
    isOwner:
      data?.owner?.toLowerCase() === account.address?.toLowerCase() &&
      !!Number(data?.balance),
    isCorrectChain: account.chainId === returnChain(chain),
    loading: !data,
    owner: data?.owner,
    chain: returnChain(chain),
  };
};
